import React, { Component } from "react";
import cn from "classnames";
import { allYears as yrs, minYear, maxYear } from "./stubs/gen-years";
import BubbleInfo from "./components/bubble-info";
import YearsDisplayHorizontal from "./components/years-display-horizontal";
import TimelineEvents from "./components/timelineEvents";
import { clearTimelineControls } from "./components/timelineControllers";
import TourZO from "./components/tour-zo";
import moment from "moment";
import Audio from "./components/new-player/audio";
import TourGrid from "./components/tours/tourGrid";
import LyricsModal from "./components/lyrics-modal";
import PhotoModal from "./components/photo-modal";
import { Pages } from "./components/timeline-pages";
import PlaylistManager from "./components/new-player/playlist-manager";
import { getCachedImageUrl } from "./services/image-cache";
import {
  sortedItems,
  getAlbumById,
  getTimelineTopHalfContentByYear,
  getTimelineZOEventById,
  getTracksByYear,
  getAlbumsFromCabinet,
} from "./drawer-items";
import { withRouter } from "react-router-dom";
import Easing from "./util/easing";
import { monthNames, monthName } from "./util/months";
import clamp from "./util/clamp";
import MusicPlayer from "./components/music-player";
import {
  setCurrentDate,
  setCurrentTrack,
  getCurrentTrack,
} from "./currentDate";
import {
  albumIsLockedByID,
  filmIsLockedByID,
  hasNotUnlimitedAccess,
} from "./user";
import { updateToggle } from "./actions/toggles";
import TimelineBasicToggles from "./components/timeline-basic-toggles";

import TL_Basic_tile from "../images/timeline-basic/TL_Basic_tile.png";
import playButton from "../images/timeline-basic/PLAY_normal.png";
import concertBg from "../images/timeline-basic/concert_ticket_bg.png";
import label from "../images/timeline/NYA_Button_Release_Solo.webp";
import { albums } from "./stubs/gen-albums";
import { connect } from "react-redux";
import SplashScreen from "./splash-screen";

let allYears = yrs.slice(); /* copy so we can add spacers */
allYears.unshift({ spacer: true, id: -1 });
allYears.push({ spacer: true, id: -2 });

const yearCount = allYears.length;
const maxYearIndex = allYears.length - 2;
const DIFF_IN_DAYS = 15;
const PAGE_WIDTH = 1080;
const ALBUM_WIDTH = 130;

const pagePercentage =
  (1 / (yearCount - 1)) * 100; /* percentage width of a single page */

let pageHeightToWidth = PAGE_WIDTH / 980;

/* we don't actually view from 0 to 100%, it is inset a bit
 * to handle the edge cases at the beginning and end of the timeline
 * */
const MIN_PERCENT = pagePercentage * 2;

/* the max percentage position that keeps the right edge
   of the last page flush to right edge of the viewport */
function getMaxPercent() {
  let { width, pageW } = Pages.measure(allYears, pageHeightToWidth);

  let pct = (maxYearIndex / (yearCount - 1)) * 100;
  let pw = width / pageW - 2;

  return pct - pw * pagePercentage;
}

//percentage value in the timeline for a given year
function getPercent(year) {
  //handle the edge cases of the first and last pages
  if (year === minYear) return MIN_PERCENT;
  if (year === maxYear) return getMaxPercent();

  let index = allYears.findIndex((y) => {
    return y.year === year;
  });
  let pct = (index / (yearCount - 1)) * 100;
  return pct;
}

//percentage to adjust the initial position when we link in
function getCenteringAdjustment(year, monthName = "jan") {
  if (year === minYear) return 0;

  let monthIdx = monthNames.indexOf(monthName);
  let monthAdjust = (pagePercentage / 12) * monthIdx;

  let count = Pages.getVisiblePageCount(allYears, pageHeightToWidth);
  let margin = (count - 1) / 2; /* amount of padding to left of item */
  let leftSpace = margin * pagePercentage;

  let fudge = -pagePercentage * 0.5; /* fudge by half a page to uncenter. */
  fudge += monthAdjust; /* adjust over to the current month */
  fudge += pagePercentage / 24; /* adjust to center over the current month */

  return pagePercentage - leftSpace + fudge;
}

function currentYear(pct) {
  return allYears[currentIndex(pct)];
}
function currentIndex(pct) {
  let len = allYears.length - 1;
  return Math.floor(len * (pct / 100));
}

function currentMonth(pct) {
  let len = allYears.length - 1;
  let idx = len * (pct / 100);
  let rem = idx - Math.floor(idx);
  let num = Math.floor(rem * 12);
  return monthNames[num];
}

function calcAlbumPosition(album) {
  if (!album) return;
  
  // Use releaseDate as the single source of truth
  if (album.releaseDate && album.releaseDate.month) {
    return (album.releaseDate.month / 12) * 100;
  }
  
  return 0;
}

const MAX_ALBUM_POSITION =
  ((PAGE_WIDTH - (ALBUM_WIDTH * 1.5 + 20)) / PAGE_WIDTH) * 100;

const AlbumPositionType = {
  CURRENT: "current",
  PREVIOUS: "previous",
};

function albumPositioner(seen) {
  return function (album, positionType) {
    let pos = Math.floor(calcAlbumPosition(album) / 5) * 5;

    if (positionType === AlbumPositionType.PREVIOUS) {
      pos -= 100;
    }

    // Check if this position is taken in first row
    if (seen[pos]) {
      // Check if second row is also taken
      if (seen[pos + 0.1]) {
        // Both rows taken, shift right
        let shiftCount = 0;
        while ((seen[pos] || seen[pos + 0.1]) && shiftCount < 20) {
          pos += 5;
          shiftCount++;
        }
        // Place in whichever row is free
        if (seen[pos]) {
          Object.defineProperty(album, 'top', {
            value: 160,
            writable: true,
            enumerable: false,
            configurable: true
          });
          seen[pos + 0.1] = true;
        } else {
          Object.defineProperty(album, 'top', {
            value: 20,
            writable: true,
            enumerable: false,
            configurable: true
          });
          seen[pos] = true;
        }
      } else {
        // First row taken, second row free
        Object.defineProperty(album, 'top', {
          value: 160,
          writable: true,
          enumerable: false,
          configurable: true
        });
        seen[pos + 0.1] = true;
      }
    } else {
      // First row free
      Object.defineProperty(album, 'top', {
        value: 20,
        writable: true,
        enumerable: false,
        configurable: true
      });
      seen[pos] = true;
    }
    
    return pos;
  };
}

const AlbumDisplayMode = {
  RELEASE: "release",
  COMPLETION: "completion",
};

function determineAlbumVisibility(album = {}, mode = AlbumDisplayMode.RELEASE) {
  if (!album) return "none";
  if (album.hasOwnProperty("releaseAlbum")) {
    return album.releaseSticker ? "block" : "none";
  }
  // Albums with completionAlbum are duplicates for COMPLETION mode only
  // The original album (without this flag) appears at releaseDate in RELEASE mode
  if (album.hasOwnProperty("completionAlbum")) {
    return mode === AlbumDisplayMode.COMPLETION ? "block" : "none";
  }
  if (mode === AlbumDisplayMode.COMPLETION) {
    return album.completionDateExist ? "none" : "block";
  }
  if (album.emptyReleaseDate) {
    return "none";
  }
  return "block";
}

/* calculates the amount other albums overlap the given album
 * so that we know where to position the info bubble */
function coveringAmount(index, items, seen) {
  let albumWidth = ALBUM_WIDTH;
  let pageWidth = PAGE_WIDTH;
  let stepSize = 0.05 * pageWidth;

  let amount = stepSize;
  let position = items[index].position + 5;
  let curr = seen[position];

  while (amount < albumWidth) {
    if (curr) {
      return (amount / albumWidth) * 100 + 5;
    }
    position += 5;
    curr = seen[position];
    amount += stepSize;
  }

  let width = Math.min(albumWidth, amount);
  return (width / albumWidth) * 100;
}

/* we need to keep the album within the page with enough room on the
   right to show the whole album and its drop shadow. can't overlap
   the page to the right due to stacking order issues, so we push
   items that /would/ overlap on the right into the next page and
   position them appropiately */
let albumTable = { 11: {}, 10: {}, "01": {}, "00": {} };
function clearAlbumTableCache() {
  albumTable = { 11: {}, 10: {}, "01": {}, "00": {} };
}
function getAlbumsDisplayedByYear(year, original, additional) {
  // Basic timeline ignores completion parameter - always uses release dates
  let key = (original ? "1" : "0") + (additional ? "1" : "0");
  if (!albumTable[key][year]) {
    let prev = sortedItems(getTimelineTopHalfContentByYear(year - 1) || []);
    let curr = sortedItems(getTimelineTopHalfContentByYear(year) || []);

    const filterItems = (items) =>
      items.filter(
        (item) => {
          return item.type !== "album" ||
            (original && item.original) ||
            (additional && item.additional) ||
            item.bootLeg;
        }
      );

    prev = filterItems(prev);
    curr = filterItems(curr);

    // Use separate tracking for prev and curr to avoid conflicts
    let seenPrev = {};
    const positionerPrev = albumPositioner(seenPrev);
    
    let seenCurr = {};
    const positionerCurr = albumPositioner(seenCurr);

    prev = prev.map((album) => {
      const pos = positionerPrev(album, AlbumPositionType.PREVIOUS);
      const originalPos = pos + 100; // Store original position before -100 adjustment
      return {
        album,
        position: pos,
        originalPosition: originalPos
      };
    });

    curr = curr.map((album) => ({
      album,
      position: positionerCurr(album, AlbumPositionType.CURRENT),
    }));

    let maxCurrPosition = MAX_ALBUM_POSITION;
    let maxPrevPosition = MAX_ALBUM_POSITION - 100;

    // Clamp curr positions to keep albums visible
    const hardLimit = 90; // Maximum visible position
    curr = curr.map(({ album, position }) => {
      const clampedPosition = Math.min(position, hardLimit);
      if (position !== clampedPosition) {
        console.log("%c @@@ Clamping album position:", "color:orange;", {
          title: album.title,
          originalPosition: position,
          clampedPosition: clampedPosition
        });
      }
      return { album, position: clampedPosition };
    });

    // After clamping, resolve conflicts where multiple albums have the same position
    const seenPositions = {};
    curr = curr.map(({ album, position }) => {
      let adjustedPos = position;
      const step = 5; // Move in 5% increments
      
      // If position is taken, move left until we find a free spot
      while (seenPositions[adjustedPos] && adjustedPos >= 0) {
        adjustedPos -= step;
      }
      
      // If we couldn't find a spot moving left and original was at hardLimit, try second row
      if (adjustedPos < 0 && position === hardLimit) {
        adjustedPos = hardLimit;
        // Mark this as second row
        Object.defineProperty(album, 'top', {
          value: 160,
          writable: true,
          enumerable: false,
          configurable: true
        });
      }
      
      seenPositions[adjustedPos] = true;
      
      if (adjustedPos !== position) {
        console.log("%c @@@ Resolved position conflict:", "color:purple;", {
          title: album.title,
          originalPosition: position,
          adjustedPosition: adjustedPos,
          top: album.top
        });
      }
      
      return { album, position: adjustedPos };
    });

    // Only carry over albums that are near the right edge AND would actually overflow
    // Albums should only carry over if their ORIGINAL position was beyond hardLimit
    prev = prev.filter(({ position, album, originalPosition }) => {
      const wasOnSecondRow = album.top === 160;
      const hardLimit = 90;
      const originallyBeyondEdge = originalPosition > hardLimit;
      
      if (album.title === 'Before And After') {
        console.log("%c @@@ Before And After carryover check:", "color:red;", {
          originalPosition,
          adjustedPosition: position,
          maxPrevPosition,
          hardLimit,
          originallyBeyondEdge,
          wasOnSecondRow
        });
      }
      
      // Only carry over if originally beyond the hard edge (90%)
      if (!originallyBeyondEdge) {
        return false;
      }
      
      // Don't carry over albums with adjusted position less than the clamp limit (90%)
      // This prevents duplicates where an album is clamped to 90% in one year
      // and appears at its adjusted position in the next year
      const clampLimit = 90;
      if (position < clampLimit) {
        console.log("%c @@@ Filtered out carryover - position below clamp limit:", "color:orange;", {
          title: album.title,
          originalPosition,
          adjustedPosition: position,
          clampLimit
        });
        return false;
      }
      
      return true;
    });

    // Deduplicate: keep curr items, remove any prev items with same ID
    const currIds = new Set(curr.map(({ album }) => album.id));
    prev = prev.filter(({ album }) => {
      const isDuplicate = currIds.has(album.id);
      if (isDuplicate) {
        console.log("%c @@@ DUPLICATE in prev REMOVED:", "color:red;", album.title, album.id);
      }
      if (album.title === 'Before And After') {
        console.log("%c @@@ Before And After dedup check:", "color:orange;", {
          title: album.title,
          id: album.id,
          isDuplicate,
          currIds: Array.from(currIds)
        });
      }
      return !isDuplicate;
    });
    
    let items = prev.concat(curr);

    // Set position on album for rendering, but make it configurable so it can be recalculated
    items.forEach((e) => {
      Object.defineProperty(e.album, 'position', {
        value: e.position,
        writable: true,
        enumerable: false,
        configurable: true
      });
    });

    // Merge seen objects for covering calculation
    let seenMerged = {...seenPrev, ...seenCurr};
    let covering = items.map((_, index) => coveringAmount(index, items, seenMerged));

    albumTable[key][year] = { items, covering };
  }

  return albumTable[key][year];
}

var trackBins = {};
function getBinnedTracksForYear(year) {
  if (trackBins[year]) return trackBins[year];

  let tracks = getTracksByYear(year) || [];

  tracks = tracks.filter((t) => !t.listOnly && !t.hiddenTrack);

  let months = [[], [], [], [], [], [], [], [], [], [], [], []];
  tracks.forEach((t) => {
    let { month } = t.releaseDate;
    months[month - 1].push(t);
  });

  months = months.map((tracks, idx) => {
    let half_weeks = [[], [], [], [], [], [], [], []];
    tracks.forEach((track) => {
      let bin = Math.min(7, Math.floor(track.releaseDate.day / 3.5));
      half_weeks[bin].push(track);
    });

    let month = monthNames[idx];
    return half_weeks.map((tracks, idx) => {
      return { tracks, date: { year, month, day: idx * 4 } };
    });
  });

  trackBins[year] = months.flat();

  return trackBins[year];
}

class TimelineYearsContainer extends Component {
  constructor(props, b) {
    super(props, b);

    const searchParams = new URLSearchParams(props.location.search);
    let trackFromDrawer = getCurrentTrack();
    let position = null,
      year = null,
      album = null;

    year = searchParams.has("year")
      ? parseInt(searchParams.get("year"), 10)
      : minYear;
    let month = searchParams.has("month") ? searchParams.get("month") : "jul";

    if (month.match(/\d+/)) month = monthName(parseInt(month, 10));

    if (trackFromDrawer) {
      setCurrentTrack(trackFromDrawer);
    } else {
      setCurrentDate({ year, month });
    }

    if (!Number.isFinite(year)) year = minYear;
    position = getPercent(year) + getCenteringAdjustment(year, month);
    year = { year };

    let min = MIN_PERCENT;
    let max = getMaxPercent();

    if (searchParams.has("position")) {
      let pos = parseFloat(searchParams.get("position"));
      if (Number.isFinite(pos)) {
        position = pos;
      }
    }

    position = Math.min(Math.max(position, min), max);

    if (!year) {
      year = currentYear(position);
    }

    let selectedEventId = null,
      photoModalState = null;

    if (searchParams.has("photo") && searchParams.has("event")) {
      selectedEventId = query.event;

      let event = getTimelineZOEventById(searchParams.get("event"));
      let collection = event.photos;
      let paramsPhoto = searchParams.get("photo");
      let current = collection.find((photo) => photo.id === paramsPhoto);

      photoModalState = { current, collection };
    }

    let selectedAlbumId = null;

    if (searchParams.has("album") || searchParams.has("currAlbum")) {
      selectedAlbumId =
        searchParams.get("album") || searchParams.get("currAlbum");
    }

    if (searchParams.has("position")) {
      let pos = parseFloat(searchParams.get("position"));
      if (Number.isFinite(pos)) position = pos;
    }

    let roomOnRight = true;

    this.state = {
      position,
      year,
      album,
      photoModalState,
      selectedEventId,
      selectedAlbumId,
      roomOnRight,
      isDragging: false,
      dragEndTime: 0,
    };

    this.onScroll = this.onScroll.bind(this);
    this.clickAlbum = this.clickAlbum.bind(this);
    this.hoverAlbum = this.hoverAlbum.bind(this);
    this.handleZoom = this.handleZoom.bind(this);
    this.zoomToDate = this.zoomToDate.bind(this);
    this.getPageContents = this.getPageContents.bind(this);
    this.scrollByPagePercentage = this.scrollByPagePercentage.bind(this);
    this.closeAlbum = this.closeAlbum.bind(this);
    this.clickViewAlbum = this.clickViewAlbum.bind(this);
    this.clickShowTracks = this.clickShowTracks.bind(this);
    this.hideBubbleAndTracks = this.hideBubbleAndTracks.bind(this);
    this.onYearClick = this.onYearClick.bind(this);
    this.updatePositionUrl = this.updatePositionUrl.bind(this);
    this.updateYearUrl = this.updateYearUrl.bind(this);
    this.clickOnEvent = this.clickOnEvent.bind(this);
    this.showEventModal = this.showEventModal.bind(this);
    this.hideEventModal = this.hideEventModal.bind(this);
    this.showVideoModal = this.showVideoModal.bind(this);
    this.showPhotoModal = this.showPhotoModal.bind(this);
    this.photoModalOnUpdate = this.photoModalOnUpdate.bind(this);
    this.hidePhotoModal = this.hidePhotoModal.bind(this);
    this.animateTo = this.animateTo.bind(this);
    this.toggleUpdate = this.toggleUpdate.bind(this);
    this.playConcertOnClick = this.playConcertOnClick.bind(this);
    this.onPageDragStart = this.onPageDragStart.bind(this);
    this.onPageDragEnd = this.onPageDragEnd.bind(this);
  }

  componentDidMount() {
    // Clear cache when component mounts to ensure fresh album data
    clearAlbumTableCache();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      const { toggles } = this.props;
      if (!toggles.basic) {
        updateToggle({ type: "basic", status: false });
      }
      clearAlbumTableCache();
    }
  }

  UNSAFE_componentWillUpdate(newprops) {
    let state = {};
    let prevParams = new URLSearchParams(this.props.location.search);
    let newParams = new URLSearchParams(newprops.location.search);
    let photo = newParams.get("photo");

    if (photo !== prevParams.get("photo")) {
      let photoModalState = null;
      if (photo) {
        let event = getTimelineZOEventById(query.event);
        let collection = event.photos;
        let current = collection.find((photo) => photo.id === query.photo);

        photoModalState = { current, collection };
      }
      state.photoModalState = photoModalState;
    }

    if (
      newParams.has("year") &&
      newParams.get("year") !== prevParams.get("year")
    ) {
      let year = newParams.get("year");
      let month = newParams.get("month");
      year = parseInt(year, 10);
      month = month || "jul";
      if (month.match(/\d+/)) month = monthName(parseInt(month, 10));
      let position = getPercent(year) + getCenteringAdjustment(year, month);
      state.position = position;
    }

    if (
      newParams.has("currAlbum") &&
      newParams.get("currAlbum") !== prevParams.get("currAlbum")
    ) {
      state.selectedEventId = null;
      state.selectedAlbumId = newParams.get("currAlbum");
    }

    if (
      newParams.has("position") &&
      newParams.get("position") !== prevParams.get("position")
    ) {
      let pos = parseFloat(newParams.get("position"));
      if (Number.isFinite(pos)) {
        state.position = pos;
      }
    }

    if (Object.keys(state).length) this.setState(state);
  }

  onYearClick(year, onDone) {
    this.jumpToDate({ year }, onDone);
  }

  jumpToDate({ year }, onDone) {
    let position = getPercent(year) + getCenteringAdjustment(year, "jul");
    this.animateTo(position, 30, onDone);
    /* this.onScroll(position)*/
  }

  animateTo(newPosition, fpsNumber, onDone = () => {}) {
    clearInterval(window.interval);
    let { position } = this.state;
    let delta = newPosition - position;
    let duration = Math.abs(delta) < pagePercentage ? 500 : 1000;
    let fps = fpsNumber;
    let frame = 1000 / fps;
    let frameCount = duration / frame;
    let step = 1.0 / frameCount;

    let current = 0;
    let currentFrame = 0;

    let interval = setInterval(() => {
      currentFrame++;
      current += step;
      let pos = Easing.easeInOutQuad(current) * delta + position;
      this.onScroll(pos);
      if (currentFrame >= frameCount) {
        clearInterval(interval);
        this.onScroll(newPosition);
        this.updatePositionUrl();
        onDone();
      }
    }, frame);
  }
  clickViewAlbum(e) {
    e.preventDefault();
    e.stopPropagation();
    let id = e.target.getAttribute("data-id");
    let album = getAlbumById(id);
    if (album && album.hasOwnProperty("isAlbum")) {
      setCurrentTrack(album);
    }

    const prevLocation = this.props.location;
    if (id.toLowerCase()[0] === "f") {
      this.props.history.push({
        pathname: "/film",
        search: `?id=${id}`, // Mantener los parámetros de consulta
        state: {
          modal: true,
          prevLocation: prevLocation,
        },
      });
    } else {
      this.props.history.push({
        pathname: "/album",
        search: `?id=${id}`,
        state: {
          modal: true,
          prevLocation: prevLocation,
        },
      });
    }
  }

  clickShowTracks(e) {
    e.preventDefault();
    e.stopPropagation();

    let id = e.target.getAttribute("data-id");
    let album = getAlbumById(id);
    let track = album.tracksForTimeline[0];
    let tid = track.id;

    //TODO - CHECK TRANSITION
    /*
    const prevLocation = this.props.location;
    this.props.history.push({
      pathname: "/timeline-months",
      search: `?viewTrack=${tid}&hl=${id}&prevTrack=${tid}`,  // Mantener los parámetros de consulta
      state: { 
        modal: true,
        prevLocation : prevLocation
      }
    });*/
    this.props.history.push(
      `/timeline-months?viewTrack=${tid}&hl=${id}&prevTrack=${tid}`
    );
  }

  roomOnRight(el) {
    let windowWidth = window.innerWidth;
    let rect = el.getBoundingClientRect();
    let { left, width } = rect;
    let remaining = windowWidth - (left + width);
    let slack = windowWidth / 5; //TODO: calculate this appropriately

    return remaining > slack;
  }
  hoverAlbum(e) {
    if (this.state.selectedAlbumId) return;

    this.clickAlbum(e);
  }
  clickAlbum(e) {
    clearTimelineControls();

    let el = e.target;
    let id = el.getAttribute("data-id");

    if (!id) return;

    let album = getAlbumById(id);
    if (album && album.hasOwnProperty("isAlbum")) {
      setCurrentTrack(album);
    }
    let roomOnRight = this.roomOnRight(el);

    let { selectedAlbumId } = this.state;

    if (id === selectedAlbumId) selectedAlbumId = null;
    else selectedAlbumId = id;

    this.setState({ selectedAlbumId, roomOnRight, selectedEventId: null });
  }

  updateQuery(newQuery, push = false) {
    const { location, history } = this.props;
    const pathname = location.pathname;

    // Crear un nuevo objeto URLSearchParams basado en la búsqueda actual
    const searchParams = new URLSearchParams(location.search);

    // Actualizar o eliminar parámetros según corresponda
    Object.keys(newQuery).forEach((k) => {
      if (newQuery[k] === null) searchParams.delete(k);
      else searchParams.set(k, newQuery[k]);
    });

    // Crear la nueva URL
    const search = searchParams.toString();
    const newUrl = `${pathname}${search ? `?${search}` : ""}`;

    // Usar history.push o history.replace según corresponda
    if (push) {
      history.push(newUrl);
    } else {
      history.replace(newUrl);
    }
  }

  updatePositionUrl() {
    const { history, location } = this.props;
    const pathname = location.pathname;
    let { position } = this.state;

    // Crear un nuevo objeto URLSearchParams
    const searchParams = new URLSearchParams();
    searchParams.set("position", position);

    // Obtener datos de posición
    let currDate = this.getCenteredYearAndMonth();
    let positionIndexMonth =
      new Date(Date.parse(currDate.month + " 1, 2012")).getMonth() + 1;

    let contentByYear = getAlbumsFromCabinet(currDate.year);

    let monthInAlbums = contentByYear.map((albums) => albums.releaseDate.month);

    let monthIndex =
      monthInAlbums.length > 0
        ? monthInAlbums.reduce(function (prev, curr) {
            return Math.abs(curr - positionIndexMonth) <
              Math.abs(prev - positionIndexMonth)
              ? curr
              : prev;
          })
        : 0;

    let albumIndex = monthInAlbums.indexOf(monthIndex);
    let centeredAlbum = contentByYear[albumIndex];

    if (centeredAlbum && centeredAlbum.hasOwnProperty("isAlbum")) {
      setCurrentTrack(centeredAlbum);
    } else {
      setCurrentDate(currDate);
    }

    const search = searchParams.toString();
    const newUrl = `${pathname}${search ? `?${search}` : ""}`;

    history.replace(newUrl);
  }

  updateYearUrl() {
    const { history, location } = this.props;
    const pathname = location.pathname;
    let query = this.getCenteredYearAndMonth();
    setCurrentDate(query);

    // Crear un nuevo objeto URLSearchParams
    const searchParams = new URLSearchParams();
    Object.keys(query).forEach((key) => {
      searchParams.set(key, query[key]);
    });

    // Crear la nueva URL
    const search = searchParams.toString();
    const newUrl = `${pathname}${search ? `?${search}` : ""}`;

    history.replace(newUrl);
  }

  onScroll(percentage) {
    let min = MIN_PERCENT;
    let max = getMaxPercent();
    let position = Math.min(Math.max(percentage, min), max);

    if (position === this.state.position) return;

    let year = currentYear(position);

    this.setState({ position, year });
  }
  showEventModal(event) {
    this.setState({ eventForModal: event });
  }
  hideEventModal() {
    this.setState({ eventForModal: null });
  }

  showVideoModal(video) {
    let prevLocation = this.props.location;
    this.props.history.push({
      pathname: "/video",
      search: `?id=${video._id}`,
      state: {
        modal: true,
        prevLocation: prevLocation,
      },
    });
  }

  showPhotoModal(event) {
    let { photos } = event;
    let current = photos[0];
    this.updateQuery({ photo: current.id, event: event.id }, true);
  }
  photoModalOnUpdate(photo) {
    this.updateQuery({ photo: photo.id });
  }

  hidePhotoModal() {
    this.props.history.goBack();
  }

  clickOnEvent(id, makeLeftOffset, makeRightOffset) {
    let { position } = this.state;
    // if (makeLeftOffset !== 0) {
    //   this.animateTo(position - 0.5,30);
    // }

    // if (makeRightOffset !== 0) {
    //   this.animateTo(position + 0.5,30);
    // }
    this.setState({ selectedAlbumId: null, selectedEventId: id });
  }
  closeAlbum() {
    this.setState({ selectedAlbumId: null });
  }
  hideBubbleAndTracks() {
    this.setState({ selectedAlbumId: null, selectedEventId: null });
  }

  renderConcerts = (concertAlbums) => {
    if (!concertAlbums.length) {
      return null;
    }
    return concertAlbums.map((album, idx) => {
      // ...existing positioning code...
      let left;
      if (concertAlbums.length - 1 === 0) {
        left = (album.position / 120) * 100 + "%";
      } else if (concertAlbums.length - 1 === 1) {
        left = album.position - 24 + "%";
      } else {
        left = 0 + "%";
      }

      const top = 10 + "px";
      const backgroundImage = `url('${concertBg}')`;
      let style = { left, top, backgroundImage };

      // Defensive checks
      const hasPlayableTracks =
        album.hasAudioTracks &&
        album.hasAudioTracks.album &&
        Array.isArray(album.hasAudioTracks.album.tracks) &&
        album.hasAudioTracks.album.tracks.length > 0;

      const firstTrack = hasPlayableTracks
        ? album.hasAudioTracks.album.tracks[0]
        : null;

      let excludeFreeAndClassicSubscription = true;
      const hasAccess =
        typeof hasNotUnlimitedAccess === "function"
          ? !hasNotUnlimitedAccess(excludeFreeAndClassicSubscription)
          : true;

      // Set the exclusion flag on the album object for the MusicPlayer
      if (album.hasAudioTracks && album.hasAudioTracks.album) {
        album.hasAudioTracks.album.excludeFreeAndClassicSubscription = true;
      }

      return (
        <div
          key={album.id}
          className="concert-ticket"
          style={style}
          onClick={() => {
            const timeSinceDrag = Date.now() - this.state.dragEndTime;
            if (this.state.isDragging || timeSinceDrag < 100) {
              return;
            }

            // Check if user has proper access
            if (!hasAccess) {
              window.unlimitedAccedV2();
              return;
            }

            // CHANGE: Play the entire concert album instead of just the first track
            if (hasPlayableTracks) {
              const currTrack = PlaylistManager.currentTrack();
              const isCurrentAlbum =
                currTrack &&
                currTrack.album &&
                currTrack.album.id === album.hasAudioTracks.album.id;

              if (isCurrentAlbum) {
                // If it's the current album, toggle play/pause
                if (Audio.getPlayerState() === "LIVEPLAY") {
                  Audio.pause();
                } else {
                  Audio.play();
                }
              } else {
                // Play the entire album from the beginning
                PlaylistManager.playAlbum(album.hasAudioTracks.album);
              }
            }
          }}
        >
          <div className="concert-info" style={{ padding: "24px" }}>
            <div className="month">
              {moment.utc(album.releaseDateTimeStamp).format("MMM")}
            </div>
            <div className="number">
              {moment.utc(album.releaseDateTimeStamp).format("DD")}
            </div>
            <div className="years">
              {moment.utc(album.releaseDateTimeStamp).format("YYYY")}
            </div>
            <div className="ticket-title">{album.title}</div>
            <div className="ticket-descrOne">
              {album.hasAudioTracks?.session}
            </div>
            <div className="concert-play-btn">
              <div className="music-player-wrapper">
                {firstTrack ? (
                  <MusicPlayer
                    item={album.hasAudioTracks.album}
                    scaled={true}
                    makeStateInSync={true}
                    disabled={!hasAccess}
                    isAlbum={true} // CHANGE: Set isAlbum to true for concert
                    playInMiddle={false}
                    isConcert={true}
                    dummy={false}
                    key={`concert-${album.id}-${firstTrack.id}`}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  getPageContents(pageProps) {
    let props = pageProps;
    const {
      toggles: {
        basic = true,
        original = false,
        bootleg: bootlegStatus = false,
        completion = false,
        additional = false,
      },
    } = this.props;
    let { year } = props.item;
    
    // Get selectedAlbumId and selectedEventId from state inside render function
    // to avoid triggering re-render of Pages component
    let { selectedAlbumId, selectedEventId, roomOnRight } = this.state;
    
    // In Basic timeline, always show ALL albums (both original and additional)
    // and ignore completion toggle - always use release dates
    let content = getAlbumsDisplayedByYear(
      year,
      true,  // Force original=true in basic timeline
      true   // Force additional=true in basic timeline
    );

    let showInfo = this.clickAlbum;
    let side = roomOnRight ? "right" : "left";

    let sorted, covering, sortedConcertAlbums;

    if (content) {
      //Hide from timeline albums
      console.log("%c @@@ BEFORE FILTERING - content.items:", "color:orange;", content.items.length, content.items);

      sortedConcertAlbums = content.items
        .filter(
          (e) =>
            e.album &&
            e.album.isConcert === true &&
            e.album.hideFromTimeLine == false &&
            e.album.hideInWeb !== true
        )
        .map(({ album }) => album);

      sorted = content.items
        .filter((e) => {
          const a = e.album;
          
          // Debug specific albums
          if (a.title && (a.title.includes('Fillmore') || a.title.includes('Archives Vol'))) {
            console.log("%c @@@ BASIC TIMELINE - CHECKING ALBUM:", "color:lime;", {
              title: a.title,
              hideInAdvancedTimelineWeb: a.hideInAdvancedTimelineWeb,
              hideInWeb: a.hideInWeb,
              hideFromTimeLine: a.hideFromTimeLine,
              'has hideFromTimeLine property': "hideFromTimeLine" in a,
              type: a.type
            });
          }
          
          // Basic view should NOT check hideInAdvancedTimelineWeb
          // Only filter by hideInWeb and hideFromTimeLine
          if (a.hideInWeb === true || a.hideInAdvancedTimelineWeb === true ) {
            console.log("%c @@@ BASIC FILTERED OUT (hideInWeb):", "color:red;", a.title);
            return false;
          }
          if ("hideFromTimeLine" in a) {
            const shouldShow = a.hideFromTimeLine === false;
            if (!shouldShow) {
              console.log("%c @@@ BASIC FILTERED OUT (hideFromTimeLine):", "color:red;", a.title, a.hideFromTimeLine);
            }
            return shouldShow;
          }
          return true;
        })
        .map(({ album }) => album);
      console.log("%c @@@ AFTER FILTERING - sorted albums:", "color:purple;", sorted.length, sorted);
      // positions = content.items.map(({ position }) => position);
      covering = content.covering;
    }

    const concertAlbums = sortedConcertAlbums.filter(
      (album) => album.isConcert
    );

    const renderedConcerts = this.renderConcerts(concertAlbums);

    // Render albums in the albums section
    const renderAlbums = () => {
      if (!content || !sorted) return null;

      console.log("%c @@@ RENDER ALBUMS - sorted before type filter:", "color:cyan;", sorted.map(a => ({title: a.title, type: a.type, id: a.id})));
      let filteredSorted = sorted.filter((album) => {
        const isAlbum = album.type === "album" || album.type === "boxset";;
        if (!isAlbum) {
          console.log("%c @@@ FILTERED OUT in render (not type='album'):", "color:red;", album.title, "type:", album.type);
        }
        return isAlbum;
      });
      console.log("%c @@@ RENDER ALBUMS - after type filter:", "color:magenta;", filteredSorted.map(a => ({title: a.title, type: a.type, id: a.id, position: a.position, display: determineAlbumVisibility(a, completion ? AlbumDisplayMode.COMPLETION : AlbumDisplayMode.RELEASE)})));

      return filteredSorted.map((album, idx) => {
        let left = album.position + "%";
        const top = album.top + "px";

        let selected = album.id === selectedAlbumId;
        let { unreleased, bootLeg } = album;
        let disabled = false;

        if (album.isFilm) {
          disabled = filmIsLockedByID(album.id);
        } else if (album.isAlbum) {
          disabled = albumIsLockedByID(album.id);
        }

        let released = album.hasOwnProperty("releaseAlbum")
          ? album.releaseSticker
            ? "released"
            : " hidden"
          : "";
        // Basic timeline ignores bootleg toggle - always shows all albums
        let bootLegItem = bootLeg ? " bootLegItem" : false;
        // Basic timeline always uses release dates, ignores completion toggle
        const display = determineAlbumVisibility(
          album,
          AlbumDisplayMode.RELEASE
        );

        console.log("%c @@@ RENDERING ALBUM:", "color:yellow;", album.title, {left, top, display, position: album.position});

        let albumBackgroundImage = album.isFilm
          ? `url('${getCachedImageUrl(album.image)}')`
          : `url('${getCachedImageUrl(album.timelineImage)}')`;
        let style = { left, display, top };

        let cx = cn("album", {
          selected,
          unreleased,
          disabled,
          released,
          [bootLegItem]: bootLegItem,
        });

        return (
          <div
            key={idx}
            className={cx}
            data-id={album.id}
            onClick={showInfo}
            onMouseEnter={undefined /*disabled && this.mouseEnterItem*/}
            onMouseLeave={undefined /*disabled && this.mouseLeaveItem*/}
            style={style}
          >
            {selected ? (
              <BubbleInfo
                side={side}
                year={year}
                spacing="110%"
                onClose={this.closeAlbum}
                object={album}
                showViewTracks={false}
                highlightedAlbumId={-1}
                onView={this.clickViewAlbum}
                onTracks={this.clickShowTracks}
              />
            ) : null}
            <div
              className={`bg ${released}`}
              style={{ backgroundImage: albumBackgroundImage }}
            >
              {/* <div className="releaseLabel" style={{backgroundImage : `url(${releaseLabel})`}}></div> */}
            </div>
          </div>
        );
      });
    };

    return (
      <div
        className="year"
        style={{ backgroundImage: `url('${TL_Basic_tile}')` }}
      >
        <div className="year-wrapper">
          <div className="year-label">{year}</div>
        </div>

        <div className="timeline-sections">
          <div className="section albums-section">
            <div className="section-content">{renderAlbums()}</div>
          </div>

          <div className="section concerts-section">
            <div className="concerts-section-content">{renderedConcerts}</div>
          </div>

          <div className="section tours-section">
            <div className="section-content">
              <TourGrid
                year={year}
                onMoreInfo={this.showEventModal}
                onPhotos={this.showPhotoModal}
                onVideo={this.showVideoModal}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
  getCenteredYearAndMonth() {
    let { position } = this.state;
    let count = Pages.getVisiblePageCount(allYears, pageHeightToWidth);
    let centeringAdjustment = (count / 2) * pagePercentage - pagePercentage;

    position = position + centeringAdjustment;

    let year = currentYear(position).year;
    let month = currentMonth(position);

    return { year, month };
  }
  handleZoom() {
    this.zoomToDate(this.getCenteredYearAndMonth());
  }

  zoomToDate(query) {
    const { history } = this.props;
    const pathname = "/timeline-months";

    // Crear un nuevo objeto URLSearchParams
    const searchParams = new URLSearchParams();
    Object.keys(query).forEach((key) => {
      searchParams.set(key, query[key]);
    });

    // Crear la nueva URL
    const search = searchParams.toString();
    const newUrl = `${pathname}${search ? `?${search}` : ""}`;

    history.push(newUrl);
  }

  // scroller for other button feature
  scrollByPagePercentage(pct) {
    let { position } = this.state;
    position += pct * pagePercentage;
    position = clamp(position, MIN_PERCENT, getMaxPercent());
    this.setState({ position });
  }

  toggleUpdate(type) {
    const { toggles } = this.props;
    const toggleState = toggles[type];

    if (type === "basic") {
      const currentSearch = this.props.location.search;

      this.props.history.push(`/timeline-advance${currentSearch}`);
    } else {
      // Clear cache before updating toggle to ensure fresh calculation
      clearAlbumTableCache();
      updateToggle({ type, status: toggleState });
    }
  }

  playConcertOnClick = (album) => {
    let excludeFreeAndClassicSubscription = true;
    let playAlbumOnClick = null;
    if (hasNotUnlimitedAccess(excludeFreeAndClassicSubscription)) {
      playAlbumOnClick = window.unlimitedAccedV2;
    } else {
      playAlbumOnClick = PlaylistManager.playAlbum(album);
    }

    return playAlbumOnClick;
  };

  onPageDragStart = () => {
    console.log("Timeline: onPageDragStart called");
    this.setState({ isDragging: true });
  };

  onPageDragEnd = (didMove, dragDuration) => {
    console.log(
      "Timeline: onPageDragEnd called, didMove:",
      didMove,
      "duration:",
      dragDuration
    );
    const shouldBlockClicks = didMove && dragDuration > 50;
    this.setState({
      isDragging: false,
      dragEndTime: shouldBlockClicks ? Date.now() : 0,
    });
  };
  render() {
    let min = MIN_PERCENT;
    let max = getMaxPercent();

    let { position, eventForModal, photoModalState, hovering } = this.state;
    const {
      toggles: {
        basic = true,
        original = false,
        bootleg = false,
        completion = false,
        additional = false,
      },
    } = this.props;
    let centeredYear = this.getCenteredYearAndMonth().year;

    let width = PAGE_WIDTH;
    let height = 980;
    let pageHeightToWidth = width / height;

    return (
      <div id="timeline-basic" className={cn("years", { hovering })}>
        <div className="pages-wrapper">
          <Pages
            pct={position}
            scrollByPagePercentage={this.scrollByPagePercentage}
            pixelScrollEnded={this.updatePositionUrl}
            pageHeightToWidth={pageHeightToWidth}
            pageWidth={width}
            pageHeight={height}
            items={allYears}
            getPageContents={this.getPageContents}
            selectedAlbumId={this.state.selectedAlbumId}
            currentItemId={this.state.selectedEventId}
            onAlbumSelect={this.clickAlbum}
            sliderMin={min}
            sliderMax={max}
            sliderOnScroll={this.onScroll}
            sliderOnEnd={this.updatePositionUrl}
            original={original}
            additional={additional}
            bootLeg={bootleg}
            completion={completion}
            hideBubbleAndTracks={this.hideBubbleAndTracks}
            animateTo={this.animateTo}
            updateYearUrl={this.updateYearUrl}
            pagePercentage={pagePercentage}
            onPageDragStart={this.onPageDragStart}
            onPageDragEnd={this.onPageDragEnd}
            isBasicTimeline={true}
          />
        </div>
        <div className="top-bar-shadow" />
        <div className="bottom-bar" />
        <div className="years-display-container">
          <div className="years-display-wrapper">
            <YearsDisplayHorizontal
              year={centeredYear}
              onClick={this.onYearClick}
            />
          </div>
        </div>
        <TimelineBasicToggles
          basic={basic}
          completion={completion}
          toggleUpdate={this.toggleUpdate}
        />

        {eventForModal && (
          <LyricsModal
            track={eventForModal}
            content="info"
            markdown={true}
            onClose={this.hideEventModal}
          />
        )}
        {photoModalState && (
          <PhotoModal
            darkBackground={true}
            photo={photoModalState.current}
            collection={photoModalState.collection}
            location={this.props.location}
            onUpdate={this.photoModalOnUpdate}
            onDismiss={this.hidePhotoModal}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = function (state) {
  if (state.status !== "drawerLoaded") {
    return {
      loaded: false,
    };
  }

  return { loaded: true, toggles: state.toggle };
};

const TimelineBasicWrapper = (props) => {
  if (!props.loaded)
    return (
      <div id="timeline-basic">
        <SplashScreen loadState={100} />
      </div>
    );
  return <TimelineYearsContainer {...props} />;
};

export default withRouter(connect(mapStateToProps)(TimelineBasicWrapper));
